/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Component Checkbox
 * Get property:
 * checked, disabled(optional) - state of checkbox
 * kind - type of checkbox
 * label(optional) - label for checkbox
 * onChange - executable callback
 *
 * @param {checked: boolean, disabled?: boolean, label: string, kind?: string, onChange: Function}
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */

import { memo, useEffect, useRef, useState } from 'react';
import type { RefObject, KeyboardEvent } from 'react';

import classNames from 'classnames';
import invoke from 'lodash/invoke';

import { KeyboardKeyEnum } from '@modules/app/appConstants';

import { CheckBoxContainerStyled } from './styled/CheckboxStyled';

export interface ICheckboxProps {
    checked: boolean;
    className?: string;
    colorBorderDisabled?: string;
    colorChecked?: string;
    colorDisabled?: string;
    dataName?: string;
    disabled?: boolean;
    id?: string;
    isNotChangeable?: boolean;
    kind: string;
    label?: string;
    onChange: (e: boolean) => void;
}

export interface IStyledProps {
    colorBorderDisabled?: string;
    colorChecked?: string;
    colorDisabled?: string;
    dataName?: string;
    isLabelProvided?: boolean;
    kind: string;
}

export const Checkbox = (
    {
        checked,
        className,
        colorBorderDisabled,
        colorChecked,
        colorDisabled,
        dataName,
        disabled,
        id,
        isNotChangeable,
        kind,
        label,
        onChange,
    }: ICheckboxProps) => {

    const [isChecked, setChecked] = useState<boolean>(checked);

    useEffect(() => {
        setChecked(checked);
    }, [checked]);

    const checkboxRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement | null>(null);

    const keyDownHandler = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === KeyboardKeyEnum.ENTER) {
            onChangeHandler();
        };
    };

    const onChangeHandler = (): void => {
        if (!disabled && !isNotChangeable) {
            setChecked(!isChecked);
            setTimeout(() => {
                onChange(!isChecked);
            });
            invoke(checkboxRef, 'current.blur');
        }
    };

    const checkboxClassName = classNames('checkbox', className ?? '', {
        'isNotChangeable': isNotChangeable,
    });

    return (
        <CheckBoxContainerStyled
            className={checkboxClassName}
            colorBorderDisabled={colorBorderDisabled}
            colorChecked={colorChecked}
            colorDisabled={colorDisabled}
            dataName={dataName}
            isLabelProvided={!!label}
            kind={kind}
            ref={checkboxRef}
            onClick={onChangeHandler}
            onKeyDown={keyDownHandler}
        >
            <input
                readOnly
                checked={isChecked}
                disabled={disabled}
                id={id}
                type='checkbox'
            />
            <label htmlFor={id}>{label}</label>
        </CheckBoxContainerStyled>
    );
};

Checkbox.defaultProps = {
    checked: false,
    disabled: false,
    kind: 'normal',
    label: '',
    onChange: () => {
    },
};

export default memo(Checkbox);
